import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../config';
import { shouldSkipCachedFeed } from '../../utils/feedFreshness';
import { isNetworkOffline } from '../../utils/requireNetwork';

export const fetchShowcasePredictions = createAsyncThunk(
  'predictionShowcase/fetchShowcasePredictions',
  async ({ page = 1, limit = 9 } = {}, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/predictions?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch showcase predictions');
      }

      return data;
    } catch (error) {
      return rejectWithValue(`Network error: ${error.message}`);
    }
  },
  {
    condition: ({ page = 1, limit = 9, force = false } = {}, { getState }) => {
      if (force) return true;
      if (isNetworkOffline()) return false;
      const state = getState().predictionShowcase;
      return !shouldSkipCachedFeed({
        force,
        fetchedAt: state.lastFetchedAt,
        hasData: state.lastFetchedAt != null,
        page,
        limit,
        cachedPage: state.lastFetchPage,
        cachedLimit: state.lastFetchLimit,
      });
    },
  }
);

const initialState = {
  predictions: [],
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  lastFetchPage: 1,
  lastFetchLimit: 9,
  pagination: {
    page: 1,
    limit: 9,
    totalPages: 0,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  }
};

const predictionShowcaseSlice = createSlice({
  name: 'predictionShowcase',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShowcasePredictions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShowcasePredictions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.predictions = action.payload.predictions || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
        const page = action.meta?.arg?.page ?? 1;
        const limit = action.meta?.arg?.limit ?? state.lastFetchLimit;
        if (page === 1) {
          state.lastFetchedAt = Date.now();
          state.lastFetchPage = page;
          state.lastFetchLimit = limit;
        }
      })
      .addCase(fetchShowcasePredictions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        if (
          (!action.meta?.arg?.page || action.meta.arg.page === 1) &&
          state.predictions.length === 0
        ) {
          state.predictions = [];
        }
      });
  },
});

export const { clearError } = predictionShowcaseSlice.actions;

export const selectPredictionShowcase = (state) => state.predictionShowcase;
export const selectShowcasePredictions = (state) => state.predictionShowcase.predictions;
export const selectPredictionShowcaseLoading = (state) => state.predictionShowcase.isLoading;
export const selectPredictionShowcaseError = (state) => state.predictionShowcase.error;
export const selectPredictionShowcasePagination = (state) => state.predictionShowcase.pagination;

export default predictionShowcaseSlice.reducer;
